import { useMemo } from "react";
import { gql } from "@apollo/client";
import { useQuery } from "@apollo/client";
import dayjs from "dayjs";

import useGetDocumentTypes from "domain/documentType/useGetDocumentTypes";

export type AiStats = {
  document_type: string;
  success_ratio: string;
  total: number;
  types_match: number;
  unknowns: number;
  unknowns_pct: number;
  color: string;
};

type QueryResult = {
  pp_ai_stats: AiStats[];
};

export const GET_AI_STATS = gql`
  query getAiStats(
    $started: timestamptz
    $ended: timestamptz
    $onlyAiDoctypes: Boolean
    $userIds: _uuid
    $orgIds: _int2
  ) {
    pp_ai_stats(
      args: {
        _started: $started
        _ended: $ended
        _only_ai_doctypes: $onlyAiDoctypes
        _orgids: $orgIds
        _crusers: $userIds
      }
      where: { _not: { document_type: { _eq: "TOTAL" } } }
    ) {
      document_type
      success_ratio
      total
      types_match
      unknowns
      unknowns_pct
    }
  }
`;

interface Variables {
  startDate?: string;
  endDate?: string;
  onlyAiDoctypes?: boolean;
  userIds?: string[];
  orgIds?: string[];
}

const useGetAiStats = ({
  startDate,
  endDate,
  onlyAiDoctypes = true,
  userIds,
  orgIds,
}: Variables): any => {
  const variables = useMemo(
    () => ({
      ...(startDate
        ? { started: startDate }
        : { started: dayjs(0).utc().format() }),
      ...(endDate ? { ended: endDate } : { ended: dayjs().utc().format() }),
      onlyAiDoctypes,
      userIds: userIds?.length ? `{${userIds?.join(",")}}` : undefined,
      orgIds: orgIds?.length ? `{${orgIds?.join(",")}}` : undefined,
    }),
    [endDate, onlyAiDoctypes, orgIds, startDate, userIds]
  );

  const queryResult = useQuery<QueryResult>(GET_AI_STATS, {
    variables,
    fetchPolicy: "network-only",
  });
  const docTypeQueryResult = useGetDocumentTypes();

  const preparedResult = useMemo(() => {
    const { data } = queryResult;
    const { data: docsType } = docTypeQueryResult;
    if (!data || !docsType) {
      return {
        ...queryResult,
        data: [],
      };
    }

    const result = data.pp_ai_stats
      .map((aiStats) => {
        const docType = docsType.find(
          (docType) => docType.name === aiStats.document_type
        );

        if (!docType) {
          return null;
        }

        return {
          ...aiStats,
          document_type: docType.translated_name,
          success_ratio: parseInt(aiStats.success_ratio || "0"),
          color: docType.meta?.color,
        };
      })
      .filter(Boolean);

    return { ...queryResult, data: result };
  }, [docTypeQueryResult, queryResult]);

  return preparedResult;
};

export default useGetAiStats;
